import {
  faChevronDown,
  faChevronUp,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import * as React from 'react';
import { SelectOption } from '../../../@types/shared';

import { InputProps } from '../../atoms/Input';
import Overlay from '../../atoms/Overlay';
import Surface from '../../atoms/Surface';
import Text from '../../atoms/Text';

import Spinner from '../../atoms/Spinner';
import InputWithIcon from '../InputWithIcon/InputWithIcon';
import styles from './AutoCompleteInput.module.scss';

export interface Props extends Omit<InputProps, 'value' | 'onChange'> {
  options: SelectOption[] | string[];
  size?: 'md' | 'sm';
  inputSize?: number;
  value?: SelectOption | string | null;
  inputValue?: string;
  onInputChange?: (newValue: string, afterSelect?: boolean) => void;
  onChange?: (
    newValue: SelectOption | string | null,
    oldValue?: SelectOption | string | null,
  ) => void;
  filterOptions?: (
    option: SelectOption | string,
    inputValue?: string,
    value?: SelectOption | string | null,
  ) => boolean;
  loading?: boolean;
}

const isSelectedOption = (
  opt: SelectOption | string,
  valueState: SelectOption | string | null,
) => {
  if (typeof opt === 'string' && typeof valueState === 'string') {
    return opt === valueState;
  }
  if (typeof opt === 'object' && typeof valueState === 'object') {
    return opt.label === valueState?.label;
  }
  return false;
};

const AutoCompleteInput = React.forwardRef<HTMLInputElement | null, Props>(
  (
    {
      id,
      className,
      style,
      type = 'text',
      onChange,
      onInputChange,
      options,
      onBlur,
      onFocus,
      value = '',
      inputValue = '',
      filterOptions,
      loading,
      ...props
    },
    ref,
  ) => {
    const [valueState, setValueState] = React.useState(value);
    const [inputState, setInputState] = React.useState(inputValue);
    const [focusedOptionIndex, setFocusedOptionIndex] = React.useState(0);
    const [showSuggestions, setShowSuggestions] = React.useState(false);

    // Handle internal and external ref
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    React.useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
      ref,
      () => inputRef.current,
    );

    const filteredOptions = React.useMemo((): SelectOption[] | string[] => {
      if (typeof options[0] === 'string') {
        return (options as string[]).filter((opt) => {
          const allowedByExternalFilter = filterOptions
            ? filterOptions(opt, inputState, valueState)
            : true;
          return (
            allowedByExternalFilter &&
            opt.toLowerCase().includes(inputState?.toLowerCase())
          );
        });
      }
      return (options as SelectOption[]).filter((opt) => {
        const allowedByExternalFilter = filterOptions
          ? filterOptions(opt, inputState, valueState)
          : true;
        return (
          allowedByExternalFilter &&
          opt.label.toLowerCase().includes(inputState?.toLowerCase())
        );
      });
    }, [filterOptions, inputState, valueState, options]);

    /**
     * Utils
     */

    const handleValueChange = React.useCallback(
      (opt: SelectOption | string | null) => {
        setValueState(opt);
        onChange?.(opt, valueState);
      },
      [onChange, valueState],
    );

    const handleInputChange = React.useCallback(
      (newValue: string, afterSelect: boolean) => {
        setInputState(newValue);
        onInputChange?.(newValue, afterSelect);
      },
      [onInputChange],
    );

    const clear = React.useCallback(() => {
      handleValueChange(null);
      handleInputChange('', false);
    }, [handleValueChange, handleInputChange]);

    const handleOptionSelected = React.useCallback(
      (opt: SelectOption | string) => {
        setShowSuggestions(false);
        handleValueChange(opt);
        if (typeof opt === 'string') {
          handleInputChange(opt, true);
        } else if (typeof opt === 'object') {
          handleInputChange(opt.label, true);
        }
        setShowSuggestions(false);
      },
      [handleInputChange, handleValueChange],
    );

    /**
     * Event handlers
     */

    const handleOnChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        handleInputChange(e.target.value, false);
      },
      [handleInputChange],
    );

    const handleOnFocus = React.useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setShowSuggestions(true);
        e.target.select();
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleOnBlur = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        setShowSuggestions(false);
        // On blur and user didn't change input value

        if (inputState.trim() === '') {
          clear();
        } else {
          // Make input state = value state, considering it might be a SelectOption or a string
          handleInputChange(
            typeof valueState === 'string'
              ? valueState
              : valueState?.label || '',
            true,
          );
        }
        onBlur?.(event);
      },
      [clear, handleInputChange, inputState, onBlur, valueState],
    );

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        const { key } = event;
        const totalButtons = filteredOptions.length;

        setShowSuggestions(true);
        const listBox = document.querySelector('[role="listbox"]');
        if (!listBox) {
          return;
        }

        let index = 0;
        let option;

        switch (key) {
          case 'ArrowUp':
            event.preventDefault();
            index = (focusedOptionIndex + totalButtons - 1) % totalButtons;
            option = listBox.querySelector(`[data-option-index="${index}"]`);
            option?.scrollIntoView({
              block: 'nearest',
            });
            setFocusedOptionIndex(index);
            break;
          case 'ArrowDown':
            event.preventDefault();
            index = (focusedOptionIndex + 1) % totalButtons;
            option = listBox.querySelector(`[data-option-index="${index}"]`);
            option?.scrollIntoView({
              block: 'nearest',
            });
            setFocusedOptionIndex(index);
            break;
          case 'Enter':
            event.preventDefault();
            handleOptionSelected(filteredOptions[focusedOptionIndex]);
            break;
          case 'Escape':
            event.preventDefault();
            setShowSuggestions(false);
            break;
          default:
            break;
        }
      },
      [
        filteredOptions,
        focusedOptionIndex,
        handleOptionSelected,
        setShowSuggestions,
        setFocusedOptionIndex,
      ],
    );

    const handleClearClick = React.useCallback(
      (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        event?.preventDefault();
        clear();
      },
      [clear],
    );

    /**
     * Sync inputState with inputValue prop in case one wants to control
     * the input state externally
     */
    React.useEffect(() => {
      setInputState(inputValue);
    }, [inputValue]);

    return (
      <div
        className={classNames(styles.wrapper, className, {
          selected: !!valueState,
        })}
        style={style}
      >
        <InputWithIcon
          autoComplete="off"
          ref={inputRef}
          type={type}
          onChange={handleOnChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          value={inputState}
          onKeyDown={handleKeyDown}
          prependIcon={inputState.length > 0 ? faTimes : undefined}
          onPrependClick={handleClearClick}
          appendIcon={!showSuggestions ? faChevronDown : faChevronUp}
          onAppendClick={() => setShowSuggestions(!showSuggestions)}
          // Prevent emitting blur event to Input
          onPrependMouseDown={(e) => e.preventDefault()}
          onAppendMouseDown={(e) => e.preventDefault()}
          {...props}
        />

        <Overlay
          show={showSuggestions}
          target={inputRef.current}
          onHide={() => undefined}
          placement="bottom-start"
          data-testid="test-overlay"
          tooltip
        >
          <Surface
            role="listbox"
            className={classNames('d-flex flex-column', styles.surface)}
            backgroundColor="grayLight1"
            padding="sm"
            style={{
              maxHeight: 250,
              overflowY: 'auto',
            }}
          >
            {!loading &&
              filteredOptions.map((opt, index) => (
                <button
                  type="button"
                  className={classNames(styles['suggestion-btn'], {
                    [styles['btn-focused']]: focusedOptionIndex === index,
                  })}
                  key={typeof opt === 'object' ? opt.id : opt}
                  data-option-index={index}
                  onClick={() => handleOptionSelected(opt)}
                  onMouseOver={() => setFocusedOptionIndex(index)}
                  onFocus={() => null}
                  // Prevent emitting blur event to Input
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Text
                    as="span"
                    size="md"
                    weight={
                      isSelectedOption(opt, valueState) ? 'bold' : 'regular'
                    }
                  >
                    {typeof opt === 'object' ? opt.label : opt}
                  </Text>
                </button>
              ))}

            {!loading && filteredOptions.length === 0 && (
              <Text as="span" size="md">
                Não há opções disponíveis para esta pesquisa.
              </Text>
            )}

            {loading && <Spinner size="sm" />}
          </Surface>
        </Overlay>
      </div>
    );
  },
);

export default AutoCompleteInput;
